import { useState, useEffect, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import { 
  Plus, Users, Phone, Calendar, DollarSign,
  Pencil, Trash2, Filter, X, Check, Clock,
  CreditCard, FileText, ChevronDown, ChevronUp,
  CheckCircle2, AlertCircle, ChevronLeft, ChevronRight,
  ArrowRightLeft, CalendarDays, RefreshCw, BarChart3,
  Link2, Copy, Package, Wallet, Search
} from "lucide-react";

const fiadoSchema = z.object({
  nome: z.string().min(2, "Mínimo 2 caracteres"),
  telefone: z.string().min(8, "Telefone inválido"),
  data_inicio: z.string().min(1, "Data obrigatória"),
  valor_total: z.coerce.number().min(0, "Valor inválido"),
  quantidade_parcelas: z.coerce.number().int().min(1, "Mínimo 1 parcela"),
  valor_parcela: z.coerce.number().min(0, "Valor inválido"),
  status: z.string(),
  situacao: z.string(),
  assinatura: z.boolean(),
  valor_assinatura: z.coerce.number().min(0, "Valor inválido").default(0),
  descricao: z.string().optional().default(""),
  tipo_pagamento: z.string().default("parcelas_fixas"),
  produto: z.string().optional().default(""),
  entrada: z.coerce.number().min(0, "Valor inválido").default(0),
});

const pagamentoSchema = z.object({
  valor: z.coerce.number().min(0.01, "Valor deve ser maior que 0"),
  data: z.string().min(1, "Data obrigatória"),
  descricao: z.string().optional().default(""),
});

const parcelaSchema = z.object({
  numero: z.coerce.number().int().min(1, "Número inválido"),
  valor: z.coerce.number().min(0, "Valor inválido"),
  status: z.string().default("pendente"),
  data_pagamento: z.string().optional().default(""),
});

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(value);
};

// Client Card with Parcelas and Summary
const FiadoCard = ({ fiado, onEdit, onDelete, onUpdateParcela, onAddPagamento, onEditPagamento, onDeletePagamento, onAddParcela, onEditParcela, onDeleteParcela, expanded, onToggleExpand, onCopyLink }) => {
  const isParcelaAberta = fiado.tipo_pagamento === "parcela_aberta";
  
  // Valor da assinatura (apenas para exibição, não entra nos cálculos)
  const valorAssinatura = fiado.valor_assinatura || 0;
  
  // Função para obter valor da parcela (individual ou default) - SEM assinatura
  const getValorParcela = (parcela) => {
    return parcela.valor || fiado.valor_parcela;
  };
  
  // Para parcelas fixas (considera valores individuais, SEM assinatura)
  const parcelasPagas = fiado.parcelas?.filter(p => p.status === "paga") || [];
  const parcelasPendentes = fiado.parcelas?.filter(p => p.status === "pendente") || [];
  const totalParcelas = fiado.parcelas?.length || 0;
  
  // Calcular valores considerando valores individuais (SEM assinatura)
  const valorPagoFixas = parcelasPagas.reduce((sum, p) => sum + getValorParcela(p), 0);
  const valorPendenteFixas = parcelasPendentes.reduce((sum, p) => sum + getValorParcela(p), 0);
  
  // Para parcela aberta
  const pagamentos = fiado.pagamentos || [];
  const totalPagoAberta = pagamentos.reduce((sum, p) => sum + p.valor, 0);
  
  // Valor com entrada abatida
  const entrada = fiado.entrada || 0;
  const valorRestante = fiado.valor_total - entrada;
  
  // Valor pago total considera entrada + pagamentos/parcelas
  const valorPagoAbertaComEntrada = totalPagoAberta + entrada;
  const valorPendenteAberta = Math.max(0, fiado.valor_total - valorPagoAbertaComEntrada);
  
  // Valores finais baseados no tipo
  const valorPago = isParcelaAberta ? totalPagoAberta : valorPagoFixas;
  const valorPendente = isParcelaAberta ? valorPendenteAberta : valorPendenteFixas;
  const valorPagoTotal = valorPago + entrada;
  
  return (
    <Card className={`transition-all ${fiado.situacao === "pago" ? "border-emerald-500/50 bg-emerald-500/5" : "border-amber-500/50"}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h3 className="font-bold text-lg truncate">{fiado.nome}</h3>
              <Badge className={fiado.situacao === "pago" ? "bg-emerald-500" : "bg-amber-500"}>
                {fiado.situacao === "pago" ? "✅ Pagou tudo" : "⏳ Pendente"}
              </Badge>
              <Badge variant="outline" className={isParcelaAberta ? "text-blue-500 border-blue-500" : "text-purple-500 border-purple-500"}>
                {isParcelaAberta ? "Parcela Aberta" : "Parcelas Fixas"}
              </Badge>
              {fiado.assinatura && (
                <Badge variant="outline" className="text-orange-500 border-orange-500">
                  Assinatura
                </Badge>
              )}
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm mb-3">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Phone className="w-4 h-4" />
                <span>{fiado.telefone}</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Calendar className="w-4 h-4" />
                <span>{fiado.data_inicio}</span>
              </div>
              {fiado.produto && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Package className="w-4 h-4 text-purple-500" />
                  <span className="truncate font-medium">{fiado.produto}</span>
                </div>
              )}
              {!isParcelaAberta && (
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-primary" />
                  <span className="font-semibold">{formatCurrency(fiado.valor_parcela)}/parcela</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 text-blue-500" />
                <span className="font-semibold">
                  {isParcelaAberta 
                    ? `${pagamentos.length} pagamento(s)` 
                    : `${parcelasPagas.length}/${totalParcelas} pagas`}
                </span>
              </div>
            </div>

            {/* Detalhamento da assinatura se houver (apenas informativo) */}
            {fiado.assinatura && valorAssinatura > 0 && (
              <div className="flex items-center gap-2 p-2 bg-orange-500/10 rounded-lg mb-3 border border-orange-500/30">
                <CreditCard className="w-4 h-4 text-orange-500" />
                <span className="text-sm text-orange-500">
                  {isParcelaAberta ? (
                    <>Assinatura mensal: <strong>{formatCurrency(valorAssinatura)}</strong></>
                  ) : (
                    <>Parcela: {formatCurrency(fiado.valor_parcela)} + Assinatura: {formatCurrency(valorAssinatura)} = <strong>{formatCurrency(fiado.valor_parcela + valorAssinatura)}</strong></>
                  )}
                </span>
              </div>
            )}

            {/* Entrada se houver */}
            {entrada > 0 && (
              <div className="flex items-center gap-2 p-2 bg-green-500/10 rounded-lg mb-3 border border-green-500/30">
                <Wallet className="w-4 h-4 text-green-500" />
                <span className="text-sm text-green-500 font-medium">
                  Entrada paga: {formatCurrency(entrada)}
                </span>
              </div>
            )}

            {/* RESUMO DE VALORES EM DESTAQUE */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs font-medium text-emerald-500">PAGO</span>
                </div>
                <p className="text-lg font-bold text-emerald-500 number-display">
                  {formatCurrency(valorPagoTotal)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {entrada > 0 && `Entrada: ${formatCurrency(entrada)}`}
                  {entrada > 0 && valorPago > 0 && " + "}
                  {isParcelaAberta ? `${pagamentos.length} pgto(s)` : `${parcelasPagas.length} parcela(s)`}
                </p>
              </div>
              
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className="text-xs font-medium text-amber-500">PENDENTE</span>
                </div>
                <p className="text-lg font-bold text-amber-500 number-display">
                  {formatCurrency(valorPendente)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isParcelaAberta ? "Falta pagar" : `${parcelasPendentes.length} parcela(s)`}
                </p>
              </div>
              
              <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-blue-500" />
                  <span className="text-xs font-medium text-blue-500">VALOR TOTAL</span>
                </div>
                <p className="text-lg font-bold text-blue-500 number-display">
                  {formatCurrency(fiado.valor_total)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {entrada > 0 ? `Restante: ${formatCurrency(valorRestante)}` : "Total contratado"}
                </p>
              </div>
            </div>
            
            {fiado.descricao && (
              <div className="p-3 bg-primary/10 rounded-lg mb-3 border-l-4 border-primary">
                <p className="text-sm font-medium flex items-start gap-2">
                  <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {fiado.descricao}
                </p>
              </div>
            )}
          </div>
          
          <div className="flex flex-col gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-blue-500 hover:text-blue-600 hover:bg-blue-500/10"
              onClick={() => onCopyLink(fiado)}
              title="Copiar link público"
              data-testid={`copy-link-btn-${fiado.id}`}
            >
              <Link2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(fiado)}
            >
              <Pencil className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive hover:text-destructive"
              onClick={() => onDelete(fiado.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Expand/Collapse Parcelas ou Pagamentos */}
        <Button
          variant="outline"
          className="w-full mt-2 justify-between"
          onClick={() => onToggleExpand(fiado.id)}
        >
          <span className="text-sm">
            {isParcelaAberta 
              ? `Ver pagamentos (${pagamentos.length})` 
              : `Ver parcelas (${parcelasPagas.length}/${totalParcelas} pagas)`}
          </span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>
        
        {expanded && (
          <>
            {isParcelaAberta ? (
              /* Lista de pagamentos para parcela aberta */
              <div className="mt-4 space-y-2">
                <Button
                  variant="default"
                  size="sm"
                  className="w-full mb-3"
                  onClick={() => onAddPagamento(fiado)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Pagamento
                </Button>
                {pagamentos.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Nenhum pagamento registrado</p>
                ) : (
                  pagamentos.map((pag) => (
                    <div
                      key={pag.id}
                      className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                          <span className="font-bold text-emerald-500">{formatCurrency(pag.valor)}</span>
                          <span className="text-xs text-muted-foreground">{pag.data}</span>
                        </div>
                        {pag.descricao && (
                          <p className="text-xs text-muted-foreground mt-1">{pag.descricao}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-primary hover:text-primary"
                          onClick={() => onEditPagamento(fiado, pag)}
                          title="Editar pagamento"
                        >
                          <Pencil className="w-3 h-3" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-destructive hover:text-destructive"
                          onClick={() => onDeletePagamento(fiado.id, pag.id)}
                          title="Excluir pagamento"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            ) : (
              /* Lista de parcelas fixas */
              <div className="mt-4">
                <Button
                  variant="default"
                  size="sm"
                  className="w-full mb-3"
                  onClick={() => onAddParcela(fiado)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Parcela
                </Button>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {fiado.parcelas?.map((parcela) => (
                    <ParcelaItem
                      key={parcela.numero}
                      parcela={parcela}
                      total={totalParcelas}
                      valorParcela={fiado.valor_parcela}
                      onUpdate={(status, data) => onUpdateParcela(fiado.id, parcela.numero, status, data)}
                      onEdit={() => onEditParcela(fiado, parcela)}
                      onDelete={() => onDeleteParcela(fiado.id, parcela.numero)}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

// Individual Parcela Item with value display
const ParcelaItem = ({ parcela, total, valorParcela, onUpdate, onEdit, onDelete }) => {
  const [showDateInput, setShowDateInput] = useState(false);
  const [date, setDate] = useState(parcela.data_pagamento || new Date().toISOString().split("T")[0]);
  
  const handleConfirmPayment = () => {
    if (!date) {
      toast.error("Informe a data do pagamento");
      return;
    }
    onUpdate("paga", date);
    setShowDateInput(false);
  };
  
  const handleMarkPending = () => {
    onUpdate("pendente", "");
    setShowDateInput(false);
  };
  
  const isPaga = parcela.status === "paga";
  
  // Usar valor individual da parcela se existir, senão usar o valor padrão
  const valorExibir = parcela.valor || valorParcela;
  
  return (
    <div className={`p-3 rounded-lg border text-center transition-all relative group ${
      isPaga ? "bg-emerald-500/10 border-emerald-500/50" : "bg-muted/50 border-border"
    }`}>
      {/* Botões de ação no hover */}
      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-primary hover:text-primary hover:bg-primary/10"
          onClick={onEdit}
          title="Editar parcela"
        >
          <Pencil className="w-3 h-3" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
          onClick={onDelete}
          title="Excluir parcela"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
      
      <p className="text-xs font-bold mb-1">
        Parcela {parcela.numero}/{total}
      </p>
      <p className="text-sm font-semibold number-display mb-2">
        {formatCurrency(valorExibir)}
      </p>
      
      {isPaga ? (
        <>
          <div className="flex items-center justify-center gap-1 text-emerald-500 mb-1">
            <Check className="w-4 h-4" />
            <span className="text-xs font-bold">PAGA</span>
          </div>
          <p className="text-xs text-muted-foreground mb-1">{parcela.data_pagamento}</p>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs w-full"
            onClick={handleMarkPending}
          >
            Desfazer
          </Button>
        </>
      ) : (
        <>
          <div className="flex items-center justify-center gap-1 text-amber-500 mb-2">
            <Clock className="w-4 h-4" />
            <span className="text-xs font-bold">PENDENTE</span>
          </div>
          
          {showDateInput ? (
            <div className="space-y-2">
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="h-7 text-xs"
              />
              <div className="flex gap-1">
                <Button
                  size="sm"
                  className="h-6 text-xs flex-1"
                  onClick={handleConfirmPayment}
                >
                  OK
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => setShowDateInput(false)}
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="default"
              size="sm"
              className="h-7 text-xs w-full"
              onClick={() => setShowDateInput(true)}
            >
              Confirmar Pgto
            </Button>
          )}
        </>
      )}
    </div>
  );
};

// Pagination component
const PaginationControls = ({ 
  currentPage, 
  totalPages, 
  totalItems, 
  startIndex, 
  endIndex, 
  onPageChange 
}) => {
  if (totalPages <= 1) return null;
  
  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 pt-4 border-t border-border">
      <p className="text-sm text-muted-foreground">
        Exibindo {startIndex + 1} - {Math.min(endIndex, totalItems)} de {totalItems} clientes
      </p>
      
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          data-testid="pagination-prev"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Anterior
        </Button>
        
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }
            
            return (
              <Button
                key={pageNum}
                variant={currentPage === pageNum ? "default" : "outline"}
                size="sm"
                className="w-8 h-8 p-0"
                onClick={() => onPageChange(pageNum)}
                data-testid={`pagination-page-${pageNum}`}
              >
                {pageNum}
              </Button>
            );
          })}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          data-testid="pagination-next"
        >
          Próximo
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>
    </div>
  );
};

export default function FiadoPage() {
  const { authAxios } = useAuth();
  const [fiados, setFiados] = useState([]);
  const [stats, setStats] = useState({ total: 0, pagos: 0, pendentes: 0 });
  const [comparativo, setComparativo] = useState({ total_pago: 0, total_pendente: 0, total_a_pagar: 0 });
  const [dadosMensais, setDadosMensais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFiado, setEditingFiado] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [activeTab, setActiveTab] = useState("pendente");
  
  // Ano e Mês state
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [mesSelecionado, setMesSelecionado] = useState(0); // 0 = Todos os meses
  const anos = Array.from({ length: 20 }, (_, i) => 2026 + i); // 2026 a 2045
  const meses = [
    { value: 0, label: "Todos" },
    { value: 1, label: "Janeiro" },
    { value: 2, label: "Fevereiro" },
    { value: 3, label: "Março" },
    { value: 4, label: "Abril" },
    { value: 5, label: "Maio" },
    { value: 6, label: "Junho" },
    { value: 7, label: "Julho" },
    { value: 8, label: "Agosto" },
    { value: 9, label: "Setembro" },
    { value: 10, label: "Outubro" },
    { value: 11, label: "Novembro" },
    { value: 12, label: "Dezembro" }
  ];
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Filters
  const [filterNome, setFilterNome] = useState("");
  const [buscaFiado, setBuscaFiado] = useState("");
  
  // Pagamento dialog state
  const [pagamentoDialogOpen, setPagamentoDialogOpen] = useState(false);
  const [selectedFiadoForPagamento, setSelectedFiadoForPagamento] = useState(null);
  const [editingPagamento, setEditingPagamento] = useState(null);

  // Parcela dialog state
  const [parcelaDialogOpen, setParcelaDialogOpen] = useState(false);
  const [selectedFiadoForParcela, setSelectedFiadoForParcela] = useState(null);
  const [editingParcela, setEditingParcela] = useState(null);

  const form = useForm({
    resolver: zodResolver(fiadoSchema),
    defaultValues: {
      nome: "",
      telefone: "",
      data_inicio: "",
      valor_total: 0,
      quantidade_parcelas: 1,
      valor_parcela: 0,
      status: "pendente",
      situacao: "pendente",
      assinatura: false,
      descricao: "",
      tipo_pagamento: "parcelas_fixas",
    },
  });

  const pagamentoForm = useForm({
    resolver: zodResolver(pagamentoSchema),
    defaultValues: {
      valor: 0,
      data: new Date().toISOString().split("T")[0],
      descricao: "",
    },
  });

  const parcelaForm = useForm({
    resolver: zodResolver(parcelaSchema),
    defaultValues: {
      numero: 1,
      valor: 0,
      status: "pendente",
      data_pagamento: "",
    },
  });

  const fetchFiados = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterNome) params.append("nome", filterNome);
      params.append("ano", anoSelecionado);
      // Mês não filtra a lista de clientes - todos aparecem sempre
      
      const res = await authAxios.get(`/fiado?${params.toString()}`);
      setFiados(res.data);
    } catch (err) {
      toast.error("Erro ao carregar clientes fiado");
    } finally {
      setLoading(false);
    }
  }, [authAxios, filterNome, anoSelecionado]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await authAxios.get(`/fiado/stats/summary?ano=${anoSelecionado}`);
      setStats(res.data);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, [authAxios, anoSelecionado]);

  const fetchComparativo = useCallback(async () => {
    try {
      const res = await authAxios.get(`/fiado/stats/comparativo?ano=${anoSelecionado}`);
      setComparativo(res.data);
    } catch (err) {
      console.error("Error fetching comparativo:", err);
    }
  }, [authAxios, anoSelecionado]);

  const fetchDadosMensais = useCallback(async () => {
    try {
      const res = await authAxios.get(`/fiado/stats/mensal?ano=${anoSelecionado}`);
      setDadosMensais(res.data);
    } catch (err) {
      console.error("Error fetching dados mensais:", err);
    }
  }, [authAxios, anoSelecionado]);

  // Atualizar tudo
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchFiados(),
        fetchStats(),
        fetchComparativo(),
        fetchDadosMensais()
      ]);
      toast.success("Dados atualizados!");
    } catch (err) {
      toast.error("Erro ao atualizar dados");
    } finally {
      setRefreshing(false);
    }
  };

  // Transferir pendentes para próximo ano
  const handleTransferirAno = async () => {
    const proximoAno = anoSelecionado + 1;
    if (!window.confirm(`Transferir clientes PENDENTES de ${anoSelecionado} para ${proximoAno}?\n\nClientes que já pagaram tudo NÃO serão transferidos.`)) return;
    
    try {
      const res = await authAxios.post(`/fiado/transferir-ano?ano_origem=${anoSelecionado}&ano_destino=${proximoAno}`);
      toast.success(res.data.message);
      fetchFiados();
      fetchStats();
      fetchComparativo();
      fetchDadosMensais();
    } catch (err) {
      toast.error("Erro ao transferir clientes");
    }
  };

  // Calculate totals from all fiados
  const calcularTotais = () => {
    let totalPago = 0;
    let totalPendente = 0;
    let totalAPagar = 0;

    fiados.forEach(fiado => {
      if (fiado.tipo_pagamento === "parcela_aberta") {
        const pagamentos = fiado.pagamentos || [];
        const pago = pagamentos.reduce((sum, p) => sum + p.valor, 0);
        totalPago += pago;
        totalPendente += Math.max(0, fiado.valor_total - pago);
      } else {
        const parcelasPagas = fiado.parcelas?.filter(p => p.status === "paga").length || 0;
        const parcelasPendentes = fiado.parcelas?.filter(p => p.status === "pendente").length || 0;
        totalPago += parcelasPagas * fiado.valor_parcela;
        totalPendente += parcelasPendentes * fiado.valor_parcela;
      }
      totalAPagar += fiado.valor_total;
    });

    return { totalPago, totalPendente, totalAPagar };
  };

  const totais = calcularTotais();

  useEffect(() => {
    fetchFiados();
    fetchStats();
    fetchComparativo();
    fetchDadosMensais();
  }, [fetchFiados, fetchStats, fetchComparativo, fetchDadosMensais]);

  const handleOpenDialog = (fiado = null) => {
    if (fiado) {
      setEditingFiado(fiado);
      form.reset({
        nome: fiado.nome,
        telefone: fiado.telefone,
        data_inicio: fiado.data_inicio,
        valor_total: fiado.valor_total,
        quantidade_parcelas: fiado.quantidade_parcelas,
        valor_parcela: fiado.valor_parcela,
        status: fiado.status,
        situacao: fiado.situacao,
        assinatura: fiado.assinatura,
        valor_assinatura: fiado.valor_assinatura || 0,
        descricao: fiado.descricao || "",
        tipo_pagamento: fiado.tipo_pagamento || "parcelas_fixas",
        produto: fiado.produto || "",
        entrada: fiado.entrada || 0,
      });
    } else {
      setEditingFiado(null);
      form.reset({
        nome: "",
        telefone: "",
        data_inicio: new Date().toISOString().split("T")[0],
        valor_total: 0,
        quantidade_parcelas: 1,
        valor_parcela: 0,
        status: "pendente",
        situacao: "pendente",
        assinatura: false,
        valor_assinatura: 0,
        descricao: "",
        tipo_pagamento: "parcelas_fixas",
        produto: "",
        entrada: 0,
      });
    }
    setDialogOpen(true);
  };

  // Pagamento functions
  const handleOpenPagamentoDialog = (fiado) => {
    setSelectedFiadoForPagamento(fiado);
    pagamentoForm.reset({
      valor: 0,
      data: new Date().toISOString().split("T")[0],
      descricao: "",
    });
    setEditingPagamento(null);
    setPagamentoDialogOpen(true);
  };

  const handleOpenEditPagamento = (fiado, pagamento) => {
    setSelectedFiadoForPagamento(fiado);
    setEditingPagamento(pagamento);
    pagamentoForm.reset({
      valor: pagamento.valor,
      data: pagamento.data,
      descricao: pagamento.descricao || "",
    });
    setPagamentoDialogOpen(true);
  };

  const handleSubmitPagamento = async (data) => {
    if (!selectedFiadoForPagamento) return;
    setSubmitting(true);
    try {
      if (editingPagamento) {
        // Editar pagamento existente
        await authAxios.put(`/fiado/${selectedFiadoForPagamento.id}/pagamento/${editingPagamento.id}`, data);
        toast.success("Pagamento atualizado!");
      } else {
        // Adicionar novo pagamento
        await authAxios.post(`/fiado/${selectedFiadoForPagamento.id}/pagamento`, data);
        toast.success("Pagamento adicionado!");
      }
      setPagamentoDialogOpen(false);
      setSelectedFiadoForPagamento(null);
      setEditingPagamento(null);
      fetchFiados();
      fetchStats();
    } catch (err) {
      toast.error(editingPagamento ? "Erro ao atualizar pagamento" : "Erro ao adicionar pagamento");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeletePagamento = async (fiadoId, pagamentoId) => {
    if (!window.confirm("Tem certeza que deseja remover este pagamento?")) return;
    try {
      await authAxios.delete(`/fiado/${fiadoId}/pagamento/${pagamentoId}`);
      toast.success("Pagamento removido!");
      fetchFiados();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao remover pagamento");
    }
  };

  const handleSubmit = async (data) => {
    setSubmitting(true);
    try {
      if (editingFiado) {
        await authAxios.put(`/fiado/${editingFiado.id}`, data);
        toast.success("Cliente atualizado!");
      } else {
        await authAxios.post("/fiado", data);
        toast.success("Cliente cadastrado!");
      }
      setDialogOpen(false);
      setEditingFiado(null);
      fetchFiados();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao salvar cliente");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este cliente?")) return;
    try {
      await authAxios.delete(`/fiado/${id}`);
      toast.success("Cliente excluído!");
      fetchFiados();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao excluir cliente");
    }
  };

  const handleUpdateParcela = async (fiadoId, parcelaNum, status, dataPagamento) => {
    try {
      const res = await authAxios.put(`/fiado/${fiadoId}/parcela/${parcelaNum}`, {
        status: status,
        data_pagamento: dataPagamento || ""
      });
      
      if (status === "paga") {
        toast.success("✅ Pagamento confirmado!");
      } else {
        toast.info("Parcela marcada como pendente");
      }
      
      // Check if all paid and switch tab
      if (res.data.situacao === "pago") {
        toast.success("🎉 Cliente pagou todas as parcelas!", {
          description: "Movido para aba 'Pagou Tudo'"
        });
        setActiveTab("pago");
      }
      
      fetchFiados();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao atualizar parcela");
    }
  };

  // Funções de gerenciamento de parcelas
  const handleAddParcela = (fiado) => {
    setSelectedFiadoForParcela(fiado);
    setEditingParcela(null);
    const nextNumero = (fiado.parcelas?.length || 0) + 1;
    parcelaForm.reset({
      numero: nextNumero,
      valor: fiado.valor_parcela || 0,
      status: "pendente",
      data_pagamento: "",
    });
    setParcelaDialogOpen(true);
  };

  const handleEditParcela = (fiado, parcela) => {
    setSelectedFiadoForParcela(fiado);
    setEditingParcela(parcela);
    parcelaForm.reset({
      numero: parcela.numero,
      valor: parcela.valor || fiado.valor_parcela || 0,
      status: parcela.status,
      data_pagamento: parcela.data_pagamento || "",
    });
    setParcelaDialogOpen(true);
  };

  const handleDeleteParcela = async (fiadoId, parcelaNum) => {
    if (!window.confirm(`Excluir a parcela ${parcelaNum}?`)) return;
    try {
      await authAxios.delete(`/fiado/${fiadoId}/parcela/${parcelaNum}`);
      toast.success("Parcela excluída!");
      fetchFiados();
      fetchStats();
    } catch (err) {
      toast.error("Erro ao excluir parcela");
    }
  };

  const handleSaveParcela = async (data) => {
    if (!selectedFiadoForParcela) return;
    try {
      if (editingParcela) {
        // Editar parcela existente
        await authAxios.put(`/fiado/${selectedFiadoForParcela.id}/parcela/${editingParcela.numero}`, {
          valor: data.valor,
          status: data.status,
          data_pagamento: data.data_pagamento || ""
        });
        toast.success("Parcela atualizada!");
      } else {
        // Adicionar nova parcela
        await authAxios.post(`/fiado/${selectedFiadoForParcela.id}/parcela`, {
          numero: data.numero,
          valor: data.valor,
          status: data.status,
          data_pagamento: data.data_pagamento || ""
        });
        toast.success("Parcela adicionada!");
      }
      setParcelaDialogOpen(false);
      fetchFiados();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar parcela");
    }
  };

  // Copiar link público
  const handleCopyLink = async (fiado) => {
    try {
      let tokenPublico = fiado.token_publico;
      
      // Se não tem token, gera um
      if (!tokenPublico) {
        const res = await authAxios.post(`/fiado/${fiado.id}/gerar-token`);
        tokenPublico = res.data.token_publico;
        // Atualiza a lista local
        fetchFiados();
      }
      
      const baseUrl = window.location.origin;
      const link = `${baseUrl}/fiado-publico/${tokenPublico}`;
      
      await navigator.clipboard.writeText(link);
      toast.success("Link copiado!", {
        description: `Link de ${fiado.nome} copiado para a área de transferência`
      });
    } catch (err) {
      console.error("Erro ao copiar link:", err);
      toast.error("Erro ao copiar link");
    }
  };

  const toggleExpand = (id) => {
    setExpandedCards(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const clearFilters = () => {
    setFilterNome("");
  };

  // Separate fiados by situacao
  const fiadosPendentes = fiados.filter(f => f.situacao === "pendente");
  const fiadosPagos = fiados.filter(f => f.situacao === "pago");

  // Pagination calculations based on active tab
  const currentList = activeTab === "pendente" ? fiadosPendentes : fiadosPagos;
  const totalItems = currentList.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedList = currentList.slice(startIndex, endIndex);

  // Reset to page 1 when items per page or tab changes
  const handleItemsPerPageChange = (value) => {
    setItemsPerPage(Number(value));
    setCurrentPage(1);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="fiado-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">Fiado</h2>
          <p className="text-muted-foreground">Controle de pagamentos pendentes e parcelados</p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="refresh-fiado-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "Atualizando..." : "Atualizar"}
          </Button>
          <Button onClick={() => handleOpenDialog()} data-testid="new-fiado-btn">
            <Plus className="w-4 h-4 mr-2" />
            Novo Cliente
          </Button>
        </div>
      </div>

      {/* Seletor de Ano/Mês e Botão de Transferência */}
      <Card className="border-2 border-blue-500/30 bg-blue-500/5">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <CalendarDays className="w-6 h-6 text-blue-500" />
              <div>
                <p className="text-sm font-bold text-blue-500">PERÍODO SELECIONADO</p>
                <p className="text-xs text-muted-foreground">Filtrando clientes por período</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3 flex-wrap">
              {/* Seletor de Ano */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Ano:</span>
                <Select value={String(anoSelecionado)} onValueChange={(v) => setAnoSelecionado(Number(v))}>
                  <SelectTrigger className="w-24" data-testid="select-ano">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {anos.map((ano) => (
                      <SelectItem key={ano} value={String(ano)}>{ano}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Seletor de Mês */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Mês:</span>
                <Select value={String(mesSelecionado)} onValueChange={(v) => setMesSelecionado(Number(v))}>
                  <SelectTrigger className="w-32" data-testid="select-mes">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {meses.map((mes) => (
                      <SelectItem key={mes.value} value={String(mes.value)}>{mes.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <Button 
                variant="outline" 
                className="border-amber-500 text-amber-600 hover:bg-amber-500/10"
                onClick={handleTransferirAno}
                data-testid="transferir-ano-btn"
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Transferir Pendentes para {anoSelecionado + 1}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comparativo Anual */}
      <Card className="border-2 border-purple-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-purple-500" />
            Comparativo Anual - {anoSelecionado}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                <span className="text-sm font-bold text-emerald-500">TOTAL PAGO</span>
              </div>
              <p className="text-2xl lg:text-3xl font-bold text-emerald-500 number-display">
                {formatCurrency(comparativo.total_pago)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{comparativo.clientes_pagos || 0} cliente(s) quitado(s)</p>
            </div>
            
            <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/30 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-bold text-amber-500">TOTAL PENDENTE</span>
              </div>
              <p className="text-2xl lg:text-3xl font-bold text-amber-500 number-display">
                {formatCurrency(comparativo.total_pendente)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{comparativo.clientes_pendentes || 0} cliente(s) devendo</p>
            </div>
            
            <div className="p-4 bg-blue-500/10 rounded-xl border border-blue-500/30 text-center">
              <div className="flex items-center justify-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-blue-500" />
                <span className="text-sm font-bold text-blue-500">TOTAL A PAGAR</span>
              </div>
              <p className="text-2xl lg:text-3xl font-bold text-blue-500 number-display">
                {formatCurrency(comparativo.total_a_pagar)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">{comparativo.quantidade_clientes || 0} cliente(s) total</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gráfico Mensal */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-primary" />
            Gráfico Mensal - {anoSelecionado}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dadosMensais} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis dataKey="mes" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(value) => `R$${value}`} />
                <Tooltip 
                  formatter={(value, name) => {
                    if (name === "total_pago") return [formatCurrency(value), "Total Pago"];
                    if (name === "total_pendente") return [formatCurrency(value), "Total Pendente"];
                    if (name === "pagou_tudo") return [value, "Pagou Tudo"];
                    return [value, name];
                  }}
                  contentStyle={{ 
                    backgroundColor: 'hsl(var(--card))', 
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend 
                  formatter={(value) => {
                    if (value === "total_pago") return "Total Pago";
                    if (value === "total_pendente") return "Total Pendente";
                    if (value === "pagou_tudo") return "Pagou Tudo";
                    return value;
                  }}
                />
                <Bar dataKey="total_pago" fill="#10b981" name="total_pago" radius={[4, 4, 0, 0]} />
                <Bar dataKey="total_pendente" fill="#f59e0b" name="total_pendente" radius={[4, 4, 0, 0]} />
                <Bar dataKey="pagou_tudo" fill="#3b82f6" name="pagou_tudo" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex justify-center gap-6 mt-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-emerald-500"></div>
              <span className="text-sm">Total Pago (R$)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-amber-500"></div>
              <span className="text-sm">Total Pendente (R$)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 rounded bg-blue-500"></div>
              <span className="text-sm">Pagou Tudo (qtd)</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Total Clientes ({anoSelecionado})</p>
                <p className="text-xl font-bold number-display">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pagou Tudo</p>
                <p className="text-xl font-bold number-display text-emerald-500">{stats.pagos}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-amber-500/10">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Pendente</p>
                <p className="text-xl font-bold number-display text-amber-500">{stats.pendentes}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filtros:</span>
            </div>
            
            <Input
              placeholder="Buscar por nome..."
              value={filterNome}
              onChange={(e) => setFilterNome(e.target.value)}
              className="w-64"
              data-testid="filter-nome"
            />
            
            {filterNome && (
              <Button variant="ghost" size="sm" onClick={clearFilters}>
                <X className="w-4 h-4 mr-1" />
                Limpar
              </Button>
            )}
            
            <div className="flex items-center gap-2 ml-auto">
              <span className="text-sm text-muted-foreground">Exibir:</span>
              <Select
                value={String(itemsPerPage)}
                onValueChange={handleItemsPerPageChange}
              >
                <SelectTrigger className="w-20" data-testid="items-per-page-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
              <span className="text-sm text-muted-foreground">por página</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs: Pendente / Pagou Tudo */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-4">
          <TabsTrigger value="pendente" className="gap-2">
            <Clock className="w-4 h-4" />
            Pendente ({fiadosPendentes.length})
          </TabsTrigger>
          <TabsTrigger value="pago" className="gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Pagou Tudo ({fiadosPagos.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pendente" className="space-y-4">
          {fiadosPendentes.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum cliente com pagamento pendente</p>
                <p className="text-sm mt-2">Clique em &quot;Novo Cliente&quot; para adicionar</p>
              </CardContent>
            </Card>
          ) : (() => {
            const filtered = buscaFiado ? fiadosPendentes.filter(f => f.nome.toLowerCase().includes(buscaFiado.toLowerCase())) : fiadosPendentes;
            return (
              <>
                <div className="flex items-center gap-1 max-w-xs">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar cliente por nome..." value={buscaFiado} onChange={(e) => setBuscaFiado(e.target.value)} className="pl-10 h-9" data-testid="search-fiado" />
                  </div>
                  {buscaFiado && (
                    <button className="shrink-0 p-1 rounded text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => setBuscaFiado("")}>
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {filtered.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">Nenhum cliente encontrado</p>
                ) : (
                  <Tabs defaultValue={filtered[0]?.id} key={filtered.map(f=>f.id).join(",")} className="w-full">
                    <div className="overflow-x-auto mb-4">
                      <TabsList className="inline-flex w-auto min-w-full h-auto flex-wrap gap-1 p-1" data-testid="fiados-pendentes-tabs">
                        {filtered.map((f) => (
                          <TabsTrigger key={f.id} value={f.id} className="gap-1.5 whitespace-nowrap text-xs" data-testid={`tab-fiado-${f.id}`}>
                            <Users className="w-3.5 h-3.5" /> {f.nome}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </div>
                    {filtered.map((fiado) => (
                      <TabsContent key={fiado.id} value={fiado.id}>
                        <FiadoCard
                          fiado={fiado}
                          onEdit={handleOpenDialog}
                          onDelete={handleDelete}
                          onUpdateParcela={handleUpdateParcela}
                          onAddPagamento={handleOpenPagamentoDialog}
                          onEditPagamento={handleOpenEditPagamento}
                          onDeletePagamento={handleDeletePagamento}
                          onAddParcela={handleAddParcela}
                          onEditParcela={handleEditParcela}
                          onDeleteParcela={handleDeleteParcela}
                          expanded={expandedCards[fiado.id]}
                          onToggleExpand={toggleExpand}
                          onCopyLink={handleCopyLink}
                        />
                      </TabsContent>
                    ))}
                  </Tabs>
                )}
              </>
            );
          })()}
        </TabsContent>

        <TabsContent value="pago" className="space-y-4">
          {fiadosPagos.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center text-muted-foreground">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhum cliente pagou tudo ainda</p>
                <p className="text-sm mt-2">Conforme os pagamentos forem confirmados, os clientes aparecerão aqui</p>
              </CardContent>
            </Card>
          ) : (() => {
            const filtered = buscaFiado ? fiadosPagos.filter(f => f.nome.toLowerCase().includes(buscaFiado.toLowerCase())) : fiadosPagos;
            return (
              <>
                <div className="flex items-center gap-1 max-w-xs">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Buscar cliente por nome..." value={buscaFiado} onChange={(e) => setBuscaFiado(e.target.value)} className="pl-10 h-9" data-testid="search-fiado-pago" />
                  </div>
                  {buscaFiado && (
                    <button className="shrink-0 p-1 rounded text-red-500 hover:text-red-400 hover:bg-red-500/10" onClick={() => setBuscaFiado("")}>
                      <X className="w-5 h-5" />
                    </button>
                  )}
                </div>
                {filtered.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">Nenhum cliente encontrado</p>
                ) : (
                  <Tabs defaultValue={filtered[0]?.id} key={filtered.map(f=>f.id).join(",")} className="w-full">
                    <div className="overflow-x-auto mb-4">
                      <TabsList className="inline-flex w-auto min-w-full h-auto flex-wrap gap-1 p-1" data-testid="fiados-pagos-tabs">
                        {filtered.map((f) => (
                          <TabsTrigger key={f.id} value={f.id} className="gap-1.5 whitespace-nowrap text-xs" data-testid={`tab-fiado-${f.id}`}>
                            <Users className="w-3.5 h-3.5" /> {f.nome}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                    </div>
                    {filtered.map((fiado) => (
                      <TabsContent key={fiado.id} value={fiado.id}>
                        <FiadoCard
                          fiado={fiado}
                          onEdit={handleOpenDialog}
                          onDelete={handleDelete}
                          onUpdateParcela={handleUpdateParcela}
                          onAddPagamento={handleOpenPagamentoDialog}
                          onEditPagamento={handleOpenEditPagamento}
                          onDeletePagamento={handleDeletePagamento}
                          onAddParcela={handleAddParcela}
                          onEditParcela={handleEditParcela}
                          onDeleteParcela={handleDeleteParcela}
                          expanded={expandedCards[fiado.id]}
                          onToggleExpand={toggleExpand}
                          onCopyLink={handleCopyLink}
                        />
                      </TabsContent>
                    ))}
                  </Tabs>
                )}
              </>
            );
          })()}
        </TabsContent>
      </Tabs>

      {/* New/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFiado ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
            <DialogDescription>
              Preencha os dados do cliente fiado
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="nome">Nome do cliente *</Label>
                <Input
                  id="nome"
                  placeholder="Nome completo"
                  data-testid="input-fiado-nome"
                  {...form.register("nome")}
                />
                {form.formState.errors.nome && (
                  <p className="text-sm text-destructive">{form.formState.errors.nome.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="telefone">Telefone *</Label>
                <Input
                  id="telefone"
                  placeholder="(00) 00000-0000"
                  data-testid="input-fiado-telefone"
                  {...form.register("telefone")}
                />
                {form.formState.errors.telefone && (
                  <p className="text-sm text-destructive">{form.formState.errors.telefone.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="data_inicio">Data de início *</Label>
                <Input
                  id="data_inicio"
                  type="date"
                  data-testid="input-fiado-data"
                  {...form.register("data_inicio")}
                />
                {form.formState.errors.data_inicio && (
                  <p className="text-sm text-destructive">{form.formState.errors.data_inicio.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Tipo de Pagamento *</Label>
                <Select
                  value={form.watch("tipo_pagamento")}
                  onValueChange={(v) => form.setValue("tipo_pagamento", v)}
                >
                  <SelectTrigger data-testid="select-tipo-pagamento">
                    <SelectValue placeholder="Selecione o tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parcelas_fixas">Parcelas Fixas (valores e datas definidas)</SelectItem>
                    <SelectItem value="parcela_aberta">Parcela Aberta (paga quando puder)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {form.watch("tipo_pagamento") === "parcela_aberta" 
                    ? "Cliente paga o valor que puder, quando puder" 
                    : "Cliente paga valor fixo em datas definidas"}
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="valor_total">Valor Total a Pagar *</Label>
                <Input
                  id="valor_total"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  data-testid="input-fiado-valor-total"
                  {...form.register("valor_total")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="entrada">Valor de Entrada</Label>
                <Input
                  id="entrada"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  data-testid="input-fiado-entrada"
                  {...form.register("entrada")}
                />
                <p className="text-xs text-muted-foreground">
                  Valor pago na hora da compra (será abatido do total)
                </p>
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="produto">Produto</Label>
                <Input
                  id="produto"
                  placeholder="Ex: TV Box 4K, Controle Remoto..."
                  data-testid="input-fiado-produto"
                  {...form.register("produto")}
                />
              </div>

              {/* Resumo do valor */}
              {(parseFloat(form.watch("valor_total") || 0) > 0 || parseFloat(form.watch("entrada") || 0) > 0) && (
                <div className="sm:col-span-2 p-3 rounded-lg bg-muted/50 border">
                  <div className="flex justify-between text-sm">
                    <span>Valor Total:</span>
                    <span>{formatCurrency(parseFloat(form.watch("valor_total") || 0))}</span>
                  </div>
                  {parseFloat(form.watch("entrada") || 0) > 0 && (
                    <div className="flex justify-between text-sm text-green-500">
                      <span>Entrada Paga:</span>
                      <span>- {formatCurrency(parseFloat(form.watch("entrada") || 0))}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-bold mt-1 pt-1 border-t">
                    <span>Restante a Pagar:</span>
                    <span className="text-amber-500">
                      {formatCurrency(Math.max(0, parseFloat(form.watch("valor_total") || 0) - parseFloat(form.watch("entrada") || 0)))}
                    </span>
                  </div>
                </div>
              )}

              {form.watch("tipo_pagamento") === "parcelas_fixas" && (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="quantidade_parcelas">Quantidade de parcelas *</Label>
                    <Input
                      id="quantidade_parcelas"
                      type="number"
                      min="1"
                      placeholder="1"
                      data-testid="input-fiado-parcelas"
                      {...form.register("quantidade_parcelas")}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="valor_parcela">Valor da parcela *</Label>
                    <Input
                      id="valor_parcela"
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0,00"
                      data-testid="input-fiado-valor-parcela"
                      {...form.register("valor_parcela")}
                    />
                  </div>
                </>
              )}

              <div className="space-y-2">
                <Label>Status</Label>
                <Select
                  value={form.watch("status")}
                  onValueChange={(v) => form.setValue("status", v)}
                >
                  <SelectTrigger data-testid="select-fiado-status">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pendente">Pendente</SelectItem>
                    <SelectItem value="pago">Pago</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Vai pagar com assinatura?</Label>
                <Select
                  value={form.watch("assinatura") ? "sim" : "nao"}
                  onValueChange={(v) => {
                    form.setValue("assinatura", v === "sim");
                    if (v === "nao") {
                      form.setValue("valor_assinatura", 0);
                    }
                  }}
                >
                  <SelectTrigger data-testid="select-fiado-assinatura">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sim">Sim</SelectItem>
                    <SelectItem value="nao">Não</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Campo de valor da assinatura - aparece quando assinatura = sim */}
              {form.watch("assinatura") && (
                <div className="space-y-2">
                  <Label htmlFor="valor_assinatura">Valor da Assinatura</Label>
                  <Input
                    id="valor_assinatura"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0,00"
                    data-testid="input-fiado-valor-assinatura"
                    {...form.register("valor_assinatura")}
                  />
                  <p className="text-xs text-muted-foreground">
                    Este valor será somado ao valor da parcela
                  </p>
                  {form.watch("tipo_pagamento") === "parcelas_fixas" && parseFloat(form.watch("valor_parcela") || 0) > 0 && (
                    <div className="p-2 bg-orange-500/10 rounded border border-orange-500/30">
                      <p className="text-sm text-orange-500 font-medium">
                        Total por parcela: {formatCurrency(parseFloat(form.watch("valor_parcela") || 0) + parseFloat(form.watch("valor_assinatura") || 0))}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        (Parcela {formatCurrency(parseFloat(form.watch("valor_parcela") || 0))} + Assinatura {formatCurrency(parseFloat(form.watch("valor_assinatura") || 0))})
                      </p>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  placeholder="Observações sobre o cliente..."
                  rows={3}
                  data-testid="input-fiado-descricao"
                  {...form.register("descricao")}
                />
              </div>
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                data-testid="submit-fiado-btn"
              >
                {submitting ? "Salvando..." : editingFiado ? "Atualizar" : "Cadastrar"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Pagamento Dialog */}
      <Dialog open={pagamentoDialogOpen} onOpenChange={setPagamentoDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPagamento ? "Editar Pagamento" : "Adicionar Pagamento"}</DialogTitle>
            <DialogDescription>
              {selectedFiadoForPagamento && (
                <>
                  Cliente: <strong>{selectedFiadoForPagamento.nome}</strong>
                  <br />
                  Valor pendente: <strong className="text-amber-500">
                    {formatCurrency(
                      Math.max(0, selectedFiadoForPagamento.valor_total - 
                      (selectedFiadoForPagamento.entrada || 0) -
                      (selectedFiadoForPagamento.pagamentos || []).reduce((sum, p) => sum + p.valor, 0))
                    )}
                  </strong>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={pagamentoForm.handleSubmit(handleSubmitPagamento)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="pag_valor">Valor do Pagamento *</Label>
              <Input
                id="pag_valor"
                type="number"
                step="0.01"
                min="0.01"
                placeholder="0,00"
                data-testid="input-pagamento-valor"
                {...pagamentoForm.register("valor")}
              />
              {pagamentoForm.formState.errors.valor && (
                <p className="text-sm text-destructive">{pagamentoForm.formState.errors.valor.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pag_data">Data do Pagamento *</Label>
              <Input
                id="pag_data"
                type="date"
                data-testid="input-pagamento-data"
                {...pagamentoForm.register("data")}
              />
              {pagamentoForm.formState.errors.data && (
                <p className="text-sm text-destructive">{pagamentoForm.formState.errors.data.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="pag_descricao">Descrição (opcional)</Label>
              <Input
                id="pag_descricao"
                placeholder="Ex: Pagamento parcial, PIX, etc."
                data-testid="input-pagamento-descricao"
                {...pagamentoForm.register("descricao")}
              />
            </div>

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setPagamentoDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                data-testid="submit-pagamento-btn"
              >
                {submitting ? "Salvando..." : (editingPagamento ? "Salvar Alterações" : "Adicionar Pagamento")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog para Adicionar/Editar Parcela */}
      <Dialog open={parcelaDialogOpen} onOpenChange={setParcelaDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingParcela ? "Editar Parcela" : "Adicionar Parcela"}
            </DialogTitle>
            <DialogDescription>
              {selectedFiadoForParcela?.nome && `Cliente: ${selectedFiadoForParcela.nome}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={parcelaForm.handleSubmit(handleSaveParcela)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="parcela-numero">Nº Parcela</Label>
                <Input
                  id="parcela-numero"
                  type="number"
                  min="1"
                  disabled={!!editingParcela}
                  {...parcelaForm.register("numero")}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="parcela-valor">Valor (R$)</Label>
                <Input
                  id="parcela-valor"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0,00"
                  {...parcelaForm.register("valor")}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select
                value={parcelaForm.watch("status")}
                onValueChange={(v) => parcelaForm.setValue("status", v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pendente">Pendente</SelectItem>
                  <SelectItem value="paga">Paga</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {parcelaForm.watch("status") === "paga" && (
              <div className="space-y-2">
                <Label htmlFor="parcela-data">Data do Pagamento</Label>
                <Input
                  id="parcela-data"
                  type="date"
                  {...parcelaForm.register("data_pagamento")}
                />
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setParcelaDialogOpen(false)}
              >
                Cancelar
              </Button>
              <Button type="submit">
                {editingParcela ? "Salvar Alterações" : "Adicionar Parcela"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
